import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { dropResource, ridKey } from '../../../shared/model/keys'
import { DEFAULT_DISCREPANCY_LIMIT, DISCREPANCY_LIMITS } from '../api/incidentsApi'
import type { IncidentsIntentState, IncidentsView } from './types'

/**
 * Намерение журналов (срок `screen`): позиция в журнале инцидентов места и
 * предел журнала расхождений. Строки журналов — в кэше RTK Query; курсоры —
 * то, что выдал сервер, без разбора.
 */
const initialState: IncidentsIntentState = { views: {}, discrepancyLimits: {} }

export interface IncidentsRef {
  resourceId: string
  registerKey: string
}

export const incidentsKey = (ref: IncidentsRef) => ridKey(ref.resourceId, ref.registerKey.toLowerCase())

const EMPTY_VIEW: IncidentsView = { cursors: [], page: 0 }
/** Глубже — уже не «листать», а разбирать: предел, чтобы намерение не росло без меры. */
const MAX_PAGES = 200

const incidentsSlice = createSlice({
  name: 'aqsi/incidents',
  initialState,
  reducers: {
    /** Следующая страница — по курсору, который сервер выдал вместе с текущей. */
    incidentsPageAdvanced(state, action: PayloadAction<IncidentsRef & { cursor: string }>) {
      const key = incidentsKey(action.payload)
      const view = state.views[key] ?? { cursors: [], page: 0 }
      if (view.page >= MAX_PAGES) return
      view.cursors = [...view.cursors.slice(0, view.page), action.payload.cursor]
      view.page += 1
      state.views[key] = view
    },
    incidentsPageReturned(state, action: PayloadAction<IncidentsRef>) {
      const view = state.views[incidentsKey(action.payload)]
      if (view !== undefined && view.page > 0) view.page -= 1
    },
    /** Начать выдачу заново: новый снимок на сервере, старые курсоры забываются. */
    incidentsRestarted(state, action: PayloadAction<IncidentsRef>) {
      delete state.views[incidentsKey(action.payload)]
    },
    discrepancyLimitChosen(state, action: PayloadAction<{ resourceId: string; limit: number }>) {
      if (!DISCREPANCY_LIMITS.includes(action.payload.limit)) return
      state.discrepancyLimits[ridKey(action.payload.resourceId, 'discrepancies')] = action.payload.limit
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      dropResource(state.views, action.payload.resourceId)
      dropResource(state.discrepancyLimits, action.payload.resourceId)
    })
  },
})

export const { incidentsPageAdvanced, incidentsPageReturned, incidentsRestarted, discrepancyLimitChosen } =
  incidentsSlice.actions
export const incidentsReducer = incidentsSlice.reducer

export function incidentsViewOf(state: IncidentsIntentState, ref: IncidentsRef): IncidentsView {
  return state.views[incidentsKey(ref)] ?? EMPTY_VIEW
}

/** Курсор текущей страницы: `null` — первая. */
export function incidentsCursorOf(view: IncidentsView): string | null {
  return view.page === 0 ? null : (view.cursors[view.page - 1] ?? null)
}

export function discrepancyLimitOf(state: IncidentsIntentState, resourceId: string): number {
  return state.discrepancyLimits[ridKey(resourceId, 'discrepancies')] ?? DEFAULT_DISCREPANCY_LIMIT
}

/** Снимок вкладки: только из разрешённых пределов. */
export function decodeDiscrepancyLimit(raw: unknown): number | undefined {
  return typeof raw === 'number' && DISCREPANCY_LIMITS.includes(raw) ? raw : undefined
}
